"use client";

import { Box, Button, colors, Stack, Typography } from "@mui/material";
import { RefreshCw } from "lucide-react";
import { useEffect } from "react";
import MuiProvider from "@v2/lib/muiProvider";
import Font from "@v2/lib/font";
import FooterCsr from "@v2/components/footer/index.csr";

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        const msg = error?.message?.toLowerCase() ?? '';
        const isAbort =
            error?.name === 'AbortError' ||
            msg.includes('aborted') ||
            msg.includes('cancelled') ||
            msg.includes('canceled');

        if (isAbort) {
            reset();
            return;
        }

        document.title = "خطا | عینک وحدت";
    }, [error, reset]);

    return (
        <html lang="fa" dir="rtl">
            <body dir="rtl" className={Font.className}>
                <MuiProvider>
                    <Box
                        display="flex"
                        flexDirection="column"
                        sx={{
                            minHeight: { xs: '100svh', md: 'unset' },
                            height: { md: '100svh' },
                            overflow: { md: 'hidden' },
                            background: `linear-gradient(180deg, ${colors.grey[50]} 0%, #fff 58%, ${colors.grey[100]} 100%)`,
                        }}
                    >
                        <Box flex={1} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: { xs: 2, sm2: 0 },
                            py: { xs: 6, md: 9 },
                            overflow: { md: 'hidden' },
                        }}>
                            <Box sx={{ width: '100%', maxWidth: 980, margin: 'auto' }}>
                                <Stack spacing={{ xs: 3, md: 4 }} alignItems="center" textAlign="center">
                                    <Box
                                        component="img"
                                        src="/assets/500-broken-glasses.svg"
                                        alt="تصویر عینک شکسته"
                                        sx={{
                                            width: { xs: 'min(100%, 620px)', md: 740 },
                                            height: 'auto',
                                            display: 'block',
                                        }}
                                    />
                                    <Typography sx={{
                                        fontSize: { xs: 16, sm: 18 },
                                        fontWeight: 300,
                                        lineHeight: 1.9,
                                        color: colors.grey[700],
                                    }}>
                                        خطایی در سرور رخ داده است
                                    </Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => window.history.back()}
                                            disableElevation
                                            sx={{
                                                borderColor: colors.grey[300],
                                                color: colors.grey[900],
                                                borderRadius: 1.5,
                                                px: 3,
                                                py: 1,
                                                '&:hover': {
                                                    borderColor: colors.grey[500],
                                                    backgroundColor: colors.grey[50],
                                                },
                                            }}
                                        >
                                            بازگشت
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={() => window.location.reload()}
                                            disableElevation
                                            startIcon={<RefreshCw size={16} />}
                                            sx={{ borderRadius: 1.5, px: 3, py: 1 }}
                                        >
                                            دوباره تلاش کنید
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Box>

                        <FooterCsr />
                    </Box>
                </MuiProvider>
            </body>
        </html>
    );
}
